import { useMemo } from "react"
import { router, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { MobileLayout, useMobileNav } from "@/components/mobile/MobileLayout"
import { HomeScreen, MemberHomeScreen } from "@/components/mobile/screens/Home"
import { InventoryScreen } from "@/components/mobile/screens/Inventory"
import { ScanScreen } from "@/components/mobile/screens/Scan"
import { ActivityScreen } from "@/components/mobile/screens/Activity"
import { ProfileScreen } from "@/components/mobile/screens/Profile"
import { AssetDetailScreen } from "@/components/mobile/screens/AssetDetail"
import type { MoveStageFormValues } from "@/components/mobile/screens/MoveStageForm"
import type {
  AssetTypeKey,
  MobileAsset,
  MobileEvent,
  MobilePageProps,
} from "@/components/mobile/types"
import { csrfToken } from "@/components/mobile/utils"

type MoveResult =
  | { ok: true }
  | { ok: false; errors: Record<string, string> }

/** Flatten Rails-shaped errors (`{ field: ["msg"] }`) into a flat string map. */
function flattenErrors(errors: unknown, t: TFunction): Record<string, string> {
  if (!errors || typeof errors !== "object") {
    return { _general: t("mobile.errors.moveGeneral") }
  }
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(errors as Record<string, unknown>)) {
    out[k] = Array.isArray(v) ? String(v[0]) : String(v)
  }
  return out
}

export default function MobileIndex() {
  const { t } = useTranslation()
  const props = usePage<MobilePageProps>().props
  const nav = useMobileNav()

  const handleLogout = () => {
    router.post("/logout", { authenticity_token: props.csrfToken || "" })
  }

  // Bulk stage move from the scan screen — one POST per asset-type group, same
  // shape the desktop scan uses (see app/frontend/pages/Scan.tsx#handleMoveAll).
  // The scan UI calls this once per type and surfaces errors to that group's
  // form. Final reload only fires when every group has succeeded.
  const handleScanConfirm = async (input: {
    asset_type: AssetTypeKey
    asset_ids: Array<MobileAsset["id"]>
    values: MoveStageFormValues
  }): Promise<MoveResult> => {
    try {
      const res = await fetch("/scan/bulk_stage_move", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          stage_id: input.values.stage_id,
          product_id: input.values.product_id,
          customer_id: input.values.customer_id,
          batch_number: input.values.batch_number,
          expiration_date: input.values.expiration_date,
          tag_ids: input.values.tag_ids,
          notes: input.values.notes,
          asset_ids: input.asset_ids,
          asset_type: input.asset_type,
        }),
      })
      const data = (await res.json().catch(() => null)) as
        | { success: boolean; errors?: Record<string, unknown> }
        | null
      if (res.ok && data?.success) {
        router.reload({
          only: [
            "assets",
            "stageStats",
            "activity",
            "recentActivity",
            "totalAssets",
            "myActivity",
          ],
        })
        return { ok: true }
      }
      return { ok: false, errors: flattenErrors(data?.errors, t) }
    } catch {
      return {
        ok: false,
        errors: { _general: t("mobile.errors.network") },
      }
    }
  }

  if (props.user.role === "regular") {
    const { user, account, myActivity, stagesByType, customers, products, tags, appVersion } = props
    return (
      <MobileLayout
        active={nav.tab}
        onTabChange={nav.setTab}
        onOpenScan={nav.openScan}
        role="regular"
        scanScreen={
          nav.scanOpen && (
            <ScanScreen
              stagesByType={stagesByType}
              customers={customers}
              products={products}
              tags={tags}
              onClose={nav.closeScan}
              onConfirm={handleScanConfirm}
            />
          )
        }
      >
        {nav.tab === "home" && (
          <MemberHomeScreen
            user={user}
            myActivity={myActivity.slice(0, 25)}
            onOpenScan={nav.openScan}
          />
        )}
        {nav.tab === "profile" && (
          <ProfileScreen
            user={user}
            account={account}
            appVersion={appVersion}
            onLogout={handleLogout}
            onChangePassword={() => router.visit("/change-password")}
          />
        )}
      </MobileLayout>
    )
  }

  // Admin tree
  const {
    user,
    account,
    totalAssets,
    stageStats,
    highlights,
    recentActivity,
    activity,
    assets,
    stages,
    stagesByType,
    assetTypes,
    products,
    containers,
    locations,
    customers,
    tags,
    appVersion,
  } = props

  const openAsset = nav.openAsset
    ? assets.find((a: MobileAsset) => String(a.id) === String(nav.openAsset!.id))
    : null

  const openAssetEvents = useMemo(() => {
    if (!openAsset) return []
    return activity.filter(
      (e: MobileEvent) => String(e.asset_id) === String(openAsset.id),
    )
  }, [activity, openAsset])

  // Single-asset stage move — the existing endpoint uses Inertia redirects
  // (success → redirect_back, failure → inertia: { errors }). router.post wraps
  // both branches so we can surface errors inline.
  const handleAssetMove = (id: MobileAsset["id"]) =>
    (values: MoveStageFormValues): Promise<MoveResult> =>
      new Promise((resolve) => {
        const payload = {
          stage_id: values.stage_id,
          product_id: values.product_id,
          customer_id: values.customer_id,
          batch_number: values.batch_number,
          expiration_date: values.expiration_date,
          notes: values.notes,
          tag_ids: values.tag_ids.map(String),
        }
        router.post(`/inventory/${id}/stage_move`, payload, {
          preserveScroll: true,
          preserveState: true,
          onSuccess: () => resolve({ ok: true }),
          onError: (errors) => resolve({ ok: false, errors: flattenErrors(errors, t) }),
        })
      })

  // Single-asset location move — `bulk_location_move` accepts an array, so we
  // pass a one-element array. Same endpoint and semantics as the desktop
  // MoveLocationDialog.
  const handleAssetMoveLocation = (id: MobileAsset["id"]) =>
    (values: { location_id: string; notes: string }): Promise<MoveResult> =>
      new Promise((resolve) => {
        router.post(
          "/inventory/bulk_location_move?asset_type=Keg",
          {
            location_id: values.location_id,
            notes: values.notes,
            asset_ids: [String(id)],
          },
          {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => resolve({ ok: true }),
            onError: (errors) => resolve({ ok: false, errors: flattenErrors(errors, t) }),
          },
        )
      })

  return (
    <MobileLayout
      active={nav.tab}
      onTabChange={nav.setTab}
      onOpenScan={nav.openScan}
      role="admin"
      assetDetail={
        openAsset && (
          <AssetDetailScreen
            asset={openAsset}
            events={openAssetEvents}
            // Pick the per-type stage list that matches this asset's type so
            // the move form filters to stages that support it and applies the
            // type-specific rules (e.g. only Keg has require_product/batch).
            stages={stagesByType[openAsset.assetable_type] ?? []}
            products={products}
            customers={customers}
            availableTags={tags}
            locations={locations}
            onClose={() => nav.setOpenAsset(null)}
            onMove={handleAssetMove(openAsset.id)}
            onMoveLocation={handleAssetMoveLocation(openAsset.id)}
          />
        )
      }
      scanScreen={
        nav.scanOpen && (
          <ScanScreen
            stagesByType={stagesByType}
            customers={customers}
            products={products}
            tags={tags}
            onClose={nav.closeScan}
            onConfirm={handleScanConfirm}
          />
        )
      }
    >
      {nav.tab === "home" && (
        <HomeScreen
          user={user}
          totalAssets={totalAssets}
          stageStats={stageStats}
          highlights={highlights}
          recentActivity={recentActivity}
          onNavigate={(t) => {
            // "scan" is an overlay (not a tab body) — route it through openScan
            // so we hit the same code path the bottom-bar FAB uses.
            if (t === "scan") nav.openScan()
            else nav.setTab(t)
          }}
          onOpenAsset={(a) => nav.setOpenAsset(a)}
        />
      )}
      {nav.tab === "inventory" && (
        <InventoryScreen
          assets={assets}
          stages={stages}
          assetTypes={assetTypes}
          products={products}
          containers={containers}
          locations={locations}
          tags={tags}
          onOpenAsset={(a) => nav.setOpenAsset(a)}
        />
      )}
      {nav.tab === "activity" && (
        <ActivityScreen
          events={activity}
          onOpenAsset={(a) => nav.setOpenAsset(a)}
        />
      )}
      {nav.tab === "profile" && (
        <ProfileScreen
          user={user}
          account={account}
          appVersion={appVersion}
          onLogout={handleLogout}
          onChangePassword={() => router.visit("/change-password")}
        />
      )}
    </MobileLayout>
  )
}
